import React, { useState, useEffect } from 'react';
import { dataService } from '../../../../Services/data/data.service';
import { makeStyles } from "@material-ui/core/styles";
import { toast } from 'react-toastify';
import { Dialog, DialogTitle, DialogContent, DialogActions, Select, MenuItem, TextField, Button } from '@mui/material';
import { getAuth } from "firebase/auth";
import { X, ArrowRight } from 'lucide-react';

export default function ChangeHistoricalPriceDialog({ dialogOpen, closeDialog, newProjectDetailRows, originalProjectDetailRows, currency, save, ...props }) {
    const classes = useStyles();
    const auth = getAuth();

    const [generalError, setGeneralError] = useState(null);

    const [allPriceChanges, setAllPriceChanges] = useState([]);

    useEffect(() => {
        // Check if price for any entry in projectDetailRows is different from that entry in originalProjectDetailRows. if so, add them to allPriceChanges with .description, .oldPrice, .newPrice
        var priceChanges = [];
        if (newProjectDetailRows && originalProjectDetailRows) {
            newProjectDetailRows.forEach((row) => {
                var originalRow = originalProjectDetailRows.find(x => x.Id === row.Id);
                if (originalRow && originalRow.price !== row.price) {
                    priceChanges.push({
                        id: row.Id,
                        description: row.description,
                        oldPrice: originalRow.price,
                        newPrice: row.price
                    })
                }
            })
            setAllPriceChanges(priceChanges);
        }
    }, [newProjectDetailRows, originalProjectDetailRows])

    const handleCloseDialog = () => {
        closeDialog();
    }

    const formatPrice = (price) => {
        // Check for null, undefined, or empty string
        if (price === null || price === undefined || price === "") {
            return "0,00";
        }

        // Convert price to a number
        const numericPrice = parseFloat(price.toString().replace(',', '.'));

        // If parseFloat fails (returns NaN), return "0,00"
        if (isNaN(numericPrice)) {
            return "0,00";
        }

        // Format the number with two decimal places and replace decimal point with a comma
        return numericPrice
            .toFixed(2) // Ensure two decimal places
            .replace('.', ',') // Replace decimal point with comma
            .replace(/\B(?=(\d{3})+(?!\d))/g, ' '); // Add space as thousands separator
    };

    return (

        <Dialog className={classes.dialog}
            open={dialogOpen}
            aria-labelledby="draggable-dialog-title"
            onClose={closeDialog}
            classes={{ paper: classes.dialogPaper }}
        >
            <div className={classes.dialog__head}>
                <DialogTitle className={classes.dialog__title} id="draggable-dialog-title">
                    Change the historical price?
                </DialogTitle>
                <button onClick={() => handleCloseDialog()} className={classes.dialog__closeButton}>
                    <X size={19} />
                </button>
            </div>
            <DialogContent className={classes.dialog__contentContainer}>
                <div className={classes.dialog__content}>
                    {generalError &&
                        <div className={classes.errorContainer}>
                            <p className={classes.errorMessage}>{generalError}</p>
                        </div>
                    }
                    <p className={classes.dialog__subtitle}>You are about to change the price of a measurement point. Do you want to change the historical price of all linked measurements?</p>
                    <div className={classes.priceChangeOverviewContainer}>
                        {allPriceChanges.map((priceChange) => {
                            return (
                                <div className={classes.priceChangeContainer}>
                                    <p className={classes.priceChange__name}>{priceChange.description}</p>
                                    <div className={classes.priceChange__priceContainer}>
                                        <p className={classes.priceChange__oldPrice}>{currency.symbol}{formatPrice(priceChange.oldPrice)}</p>
                                        <ArrowRight className={classes.icon__medium} />
                                        <p className={classes.priceChange__newPrice}>{currency.symbol}{formatPrice(priceChange.newPrice)}</p>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>
            </DialogContent>
            <DialogActions sx={{ display: 'flex', flexDirection: "column", gap: "8px", justifyContent: 'space-between', paddingLeft: '16px', paddingRight: '16px', paddingBottom: "16px" }}>
                {/* {financialTypeToEdit &&
                    <button className={classes.deleteButton} onClick={closeDialog}>Delete</button>
                } */}
                <button className={classes.deleteButton} onClick={() => save(newProjectDetailRows, true)}>Change historical price</button>
                <button className={classes.saveButton} onClick={() => save(newProjectDetailRows, false)}>Change prices from now on</button>
            </DialogActions>
        </Dialog >
    );
}


const useStyles = makeStyles(() => ({
    dialog: {
        margin: "auto"
    },

    dialogPaper: {
        width: '480px !important',
        height: 'auto',
        borderRadius: '6px !important',
    },

    dialog__head: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',

        padding: '16px 16px 0px 16px !important',
        marginBottom: '8px !important',
    },

    dialog__title: {
        fontSize: '24px !important',
        fontFamily: "Lato, sans-serif !important",
        fontWeight: 'normal',

        padding: '0px !important',
    },

    dialog__closeButton: {
        backgroundColor: '#F5F5F5',
        border: 'none',
        cursor: 'pointer',

        padding: '3px !important',
        height: '25px !important',

        borderRadius: '3px',

        transition: 'all 0.2s',

        '&:hover': {
            backgroundColor: '#D84949',
            color: '#F5F5F5',
        },

        '&:focus-visible': {
            outline: '#4654FB auto 1px',
        },
    },

    dialog__subtitle: {
        fontSize: '16px',
        fontFamily: "Lato, sans-serif",
        fontWeight: 'normal',

        color: '#0C0E29',

        margin: '0px 0px 16px 0px',
    },

    priceChangeOverviewContainer: {
        width: "100%",

        display: "flex",
        flexDirection: "column",
        gap: "8px",
    },

    priceChangeContainer: {
        width: "100%",

        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "8px",
    },

    priceChange__name: {
        fontSize: "16px",
        fontFamily: "Lato, sans-serif",
        color: "#0C0E29",

        margin: "0",
    },

    priceChange__priceContainer: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "8px",
    },

    priceChange__oldPrice: {
        fontSize: "16px",
        fontFamily: "Lato, sans-serif",
        color: "#0C0E29",
        opacity: "0.8",

        lineHeight: "100%",

        margin: "0px",
    },

    icon__medium: {
        height: "20px",
        width: "20px",
        color: "#0C0E29",

        strokeWidth: "1.6px",
    },

    priceChange__newPrice: {
        fontSize: "16px",
        fontFamily: "Lato, sans-serif",
        color: "#0C0E29",

        lineHeight: "100%",

        margin: "0px",
    },

    dialog__contentContainer: {
        padding: '0px 16px !important',
        marginBottom: "16px",

        // remove scrollbar
        '&::-webkit-scrollbar': {
            display: 'none',
        },
        '-ms-overflow-style': 'none',
        scrollbarWidth: 'none',
    },

    dialog__content: {
        display: 'flex',
        height: '100%',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',

        margin: '0px !important',
    },

    errorContainer: {
        width: "100%",
        backgroundColor: "#FDF3F3",

        border: "1px solid #FBE5E5",
        borderRadius: "6px",

        marginBottom: "16px",
        padding: "12px",
    },

    errorMessage: {
        fontSize: "16px",
        color: "#D84949"
    },

    saveButton: {
        height: "40px",
        width: "100%",
        boxSizing: "border-box",

        border: "none",
        borderRadius: "4px",

        fontSize: "16px",
        color: "white",
        backgroundColor: "#C3996B",

        padding: "0 16px",
        marginLeft: "0px !important",

        transition: "all 0.2s ease-in-out",

        userSelect: "none",

        "&:hover": {
            backgroundColor: "#a77d4f",
            cursor: "pointer",
        }
    },

    deleteButton: {
        height: "40px",
        width: "100%",
        padding: "0 16px",
        verticalAlign: 'center',

        border: "none",
        borderRadius: "4px",

        fontSize: "16px",
        color: "white",
        backgroundColor: "#D84949",

        transition: "all 0.2s ease-in-out",

        userSelect: "none",

        "&:hover": {
            backgroundColor: "#C73B3B",
            color: "white",
            border: "1px solid #C73B3B",
            cursor: "pointer",
        }
    }
}));
